<template>
  <div class="page product-detail" v-if="facts">
    <p class="breadcrumb"><a href="/products.html">匠心产品</a> / {{ facts.shortName }}</p>
    <h1>{{ facts.shortName }}</h1>
    <p class="page-intro">{{ facts.lead }}</p>

    <div class="detail-body">
      <div class="detail-media">
        <figure v-for="image in facts.images" :key="image.src" class="product-figure">
          <img class="product-image" :src="image.src" :alt="image.alt" width="480" height="480" loading="lazy">
          <figcaption>{{ image.caption }}</figcaption>
        </figure>
        <p v-if="facts.images.length === 0" class="packaging-note">
          本站尚未取得该规格的包装实拍图，因此不展示产品照片；下列参数来自该 SKU 的包装标签与产品参数表。
        </p>
        <p v-else class="packaging-note">
          产品图由该 SKU 的包装实拍图整理为站内资源，来源登记在 <code>docs/product-geo/sources-conflicts.md</code>。
        </p>
      </div>

      <div class="detail-copy">
        <h2>产品参数</h2>
        <dl class="spec-list">
          <div v-for="row in parameterRows" :key="row.label" class="spec-row">
            <dt>{{ row.label }}</dt>
            <dd>{{ row.value }}</dd>
          </div>
        </dl>

        <h2>包装标示值</h2>
        <dl v-if="facts.labelFacts.length > 0" class="spec-list">
          <div v-for="row in facts.labelFacts" :key="row.label" class="spec-row">
            <dt>{{ row.label }}</dt>
            <dd>{{ row.value }}</dd>
          </div>
        </dl>
        <p v-else class="packaging-note">该规格的包装信息面未标示成分含量数值，本站不填入未经标签确认的数字。</p>

        <h2>食用方法</h2>
        <p>{{ facts.method }}</p>

        <h2>温馨提示</h2>
        <ul>
          <li v-for="(caution, index) in facts.cautions" :key="index">{{ caution }}</li>
        </ul>

        <h2>与其他规格比较</h2>
        <p>
          <a href="/products.html">回到产品列表的参数对照表</a>，可以按净含量、配料、保质期与贮存条件横向比较石斛与天麻系列；
          同系列还有
          <template v-if="siblings.length > 0">
            <a v-for="sibling in siblings" :key="sibling.id" :href="`/products/${sibling.id}.html`" class="sibling-link">{{ sibling.shortName }}</a>。
          </template>
          <template v-else>其他规格产品。</template>
        </p>

        <h2>购买与资料查询</h2>
        <p>
          本站不公布价格与促销信息。购买渠道、当期规格与检测报告等资料，可拨打服务热线
          <a href="tel:4008317518">400-831-7518</a>，或
          <a href="/contact.html">查看联系我们</a>、<a href="/faq.html">常见问题</a>；
          包装防伪码可按包装提示核验真伪。
        </p>
        <ContactFriend placement="detail" />

        <h2>尚未公开的信息</h2>
        <ul class="gap-list">
          <li v-for="(gap, index) in facts.gaps" :key="index">{{ gap }}</li>
        </ul>
        <p class="packaging-note">{{ packagingNote }}</p>

        <p><a href="/products.html">返回全部产品</a></p>
      </div>
    </div>
  </div>
</template>

<script>
import { findProductFacts, productFacts } from '@/config/productFacts';
import { showcasedProductIds } from '@/config/products';
import ContactFriend from '@/components/ContactFriend.vue';

const PACKAGING_NOTE = '本页参数照录自该 SKU 现行包装标签；不同批次包装细节可能不同，请以收到的实物包装标示为准。';

export default {
  name: 'ProductDetailPage',
  components: {
    ContactFriend
  },
  computed: {
    facts() {
      const id = this.$route.params.id;
      return showcasedProductIds.includes(id) ? findProductFacts(id) : null;
    },
    packagingNote() {
      return PACKAGING_NOTE;
    },
    parameterRows() {
      return [
        { label: '产品名称', value: this.facts.name },
        { label: '规格', value: this.facts.spec.join('，') },
        { label: '配料', value: this.facts.ingredients },
        { label: '产品类型', value: this.facts.type },
        { label: '保质期', value: this.facts.shelfLife },
        { label: '贮存条件', value: this.facts.storage },
        { label: '产地', value: this.facts.origin },
        { label: '原料', value: this.facts.rawMaterials.join('；') }
      ];
    },
    // 同系列优先；某系列只有一个 SKU 时列出其余产品，避免该页只剩一条入链。
    siblings() {
      const others = productFacts.filter((facts) => facts.id !== this.facts.id);
      const sameCategory = others.filter((facts) => facts.category === this.facts.category);
      return sameCategory.length > 0 ? sameCategory : others.slice(0, 2);
    }
  }
}
</script>

<style scoped>
.page {
  max-width: 960px;
  margin: 0 auto;
  padding: 20px 16px 48px;
  text-align: left;
}

.breadcrumb {
  color: #666;
  font-size: 0.9em;
  margin: 0 0 8px;
}

.breadcrumb a {
  color: #4b0082;
}

h1 {
  color: #4b0082;
  font-size: 1.5em;
  margin: 0 0 8px;
}

h2 {
  color: #4b0082;
  font-size: 1.1em;
  margin: 22px 0 6px;
}

.detail-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.product-figure {
  margin: 0 0 12px;
}

.product-image {
  max-width: 100%;
  height: auto;
  border-radius: 16px;
  background: #f5f0ff;
  display: block;
}

figcaption {
  color: #666;
  font-size: 0.85em;
  margin-top: 6px;
}

.page-intro,
li,
p,
dd,
dt {
  color: #333;
  line-height: 1.8;
}

.spec-list {
  margin: 0;
  border-top: 1px solid #eee;
}

.spec-row {
  display: flex;
  gap: 12px;
  border-bottom: 1px solid #eee;
  padding: 6px 0;
}

.spec-row dt {
  flex: 0 0 6em;
  color: #666;
}

.spec-row dd {
  margin: 0;
  flex: 1;
}

ul {
  padding-left: 18px;
  margin: 0;
}

.gap-list {
  color: #666;
}

.sibling-link {
  margin-right: 10px;
}

.packaging-note {
  color: #666;
  font-size: 0.95em;
}

@media (min-width: 769px) {
  .detail-body {
    flex-direction: row;
    align-items: flex-start;
  }

  .detail-media {
    width: 44%;
    flex: 0 0 44%;
  }

  .detail-copy {
    flex: 1;
  }
}
</style>
